import { Link } from 'react-router-dom';
import CaseStudyPageNav from '../../../components/feature/CaseStudyPageNav';
import CaseStudyNav from '../../../components/feature/CaseStudyNav';
import { useSEO } from '../../../hooks/useSEO';
import JsonLd from '../../../components/feature/JsonLd';

export default function ShiseidoBeautyEmpowermentPage() {
  useSEO({
    title: 'Shiseido Beauty Empowerment Programme | Design For',
    description: 'Strategic consulting for beauty industry social impact. Research-driven strategy for customer empowerment and brand purpose.',
    keywords: 'beauty empowerment, social impact, brand purpose, customer engagement, beauty industry',
    canonical: '/work/shiseido-beauty-empowerment',
    ogType: 'article',
    ogImage: 'https://static.readdy.ai/image/7afcb539829d8ddcd28f62262d25b133/ee9b8a579b0f0dfe46dd47075426190b.jpeg',
  });

  const jsonLdData = {
    '@context': 'https://schema.org',
    '@type': 'CreativeWork',
    name: 'Strategic consulting for beauty industry social impact',
    description: 'We partnered with Shiseido to develop strategic frameworks for beauty empowerment programmes, customer engagement, and brand purpose initiatives.',
    url: 'https://example.com/work/shiseido-beauty-empowerment',
    author: {
      '@type': 'Organization',
      name: 'Design For',
      url: 'https://example.com',
    },
    publisher: {
      '@type': 'Organization',
      name: 'Design For',
      url: 'https://example.com',
    },
    about: 'Beauty company - Empowerment programme strategy',
    keywords: 'beauty empowerment, social impact, brand purpose, customer engagement, beauty industry',
  };

  return (
    <div className="min-h-screen bg-white">
      <JsonLd data={jsonLdData} />
      <CaseStudyPageNav />

      {/* Main Content */}
      <section className="pt-32 pb-20 px-6">
        <div className="max-w-6xl mx-auto">
          <Link
            to="/work"
            className="inline-flex items-center gap-2 text-sm font-medium text-gray-500 hover:text-gray-900 transition-colors mb-10"
          >
            <i className="ri-arrow-left-line"></i>
            Back to Work
          </Link>

          <div className="grid lg:grid-cols-2 gap-16 items-start">
            {/* Left: Text + Outcomes */}
            <div>
              <h1 className="text-4xl lg:text-5xl font-black text-black tracking-tight leading-tight mb-3">
                Shifting beauty from transactions to empowerment through digital
              </h1>
              <h2 className="text-lg font-medium text-primary mb-8">Shiseido</h2>

              <p className="text-base text-gray-600 leading-relaxed mb-4">
                When COVID shut down stores and face to face consultations, Shiseido had to quickly rethink how it supported customers and employees. The challenge was not just going digital, but deciding what role a global beauty brand should play in people&apos;s lives during a moment of real disruption.
              </p>
              <p className="text-base text-gray-600 leading-relaxed mb-10">
                We shifted the focus from selling products to building relationships. We designed &apos;Shiseido Empower&apos;, a digital service that connects customers with Beauty Consultants through virtual consultations, turning beauty into a source of confidence, connection, and support rather than just transactions.
              </p>

              <h3 className="text-xl font-black text-black mb-5">Outcomes</h3>
              <ul className="space-y-3">
                {[
                  'Shifted focus to new and lapsed customers beyond traditional retail audiences',
                  'Designed a virtual consultation service connecting customers with Beauty Consultants',
                  'Repositioned Beauty Consultants as trusted digital advisors rather than in store sellers',
                  'Created a simple accessible entry point via social, email and mobile',
                  'Designed an end to end journey linking advice, curated products and storytelling',
                  'Embedded a social impact model supporting the Shiseido Women\'s Foundation',
                  'Delivered a scalable pilot balancing brand trust, commercial viability and global rollout',
                ].map((item) => (
                  <li key={item} className="flex items-start gap-3 text-base text-gray-700 leading-relaxed">
                    <span className="mt-2 w-2 h-2 bg-primary rounded-full flex-shrink-0"></span>
                    {item}
                  </li>
                ))}
              </ul>
            </div>

            {/* Right: Images */}
            <div className="flex flex-col gap-6">
              <div className="rounded-2xl overflow-hidden">
                <img
                  src="https://static.readdy.ai/image/7afcb539829d8ddcd28f62262d25b133/28ae7bf51bb22294b27f1d1328abb261.jpeg"
                  alt="Shifting beauty from transactions to empowerment through digital — Shiseido"
                  className="w-full h-auto object-cover object-top"
                />
              </div>
              <div className="rounded-2xl overflow-hidden">
                <img
                  src="https://static.readdy.ai/image/7afcb539829d8ddcd28f62262d25b133/5f81f4da303f6d5ea8a099e6f49cb327.jpeg"
                  alt="Shiseido customer journey mapping workshop"
                  className="w-full h-auto object-cover object-top"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <CaseStudyNav currentSlug="shiseido-beauty-empowerment" />

      {/* Footer */}
      <footer className="bg-black py-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid md:grid-cols-3 gap-12 mb-16">
            <div className="space-y-6">
              <img
                src="https://static.readdy.ai/image/7afcb539829d8ddcd28f62262d25b133/177dc540d7c9a6700435609d623348f7.png"
                alt="Design For"
                className="h-16"
              />
              <p className="text-white/60 text-sm leading-relaxed">Strategic consulting for ambitious businesses.</p>
            </div>
            <div>
              <h4 className="text-sm uppercase tracking-wider text-white font-bold mb-6">Navigate</h4>
              <div className="space-y-3">
                <Link to="/" className="block text-white/70 hover:text-primary transition-colors text-sm font-medium">Home</Link>
                <Link to="/about" className="block text-white/70 hover:text-primary transition-colors text-sm font-medium">About</Link>
                <Link to="/services" className="block text-white/70 hover:text-primary transition-colors text-sm font-medium">Services</Link>
                <Link to="/work" className="block text-white/70 hover:text-primary transition-colors text-sm font-medium">Work</Link>
                <Link to="/resources" className="block text-white/70 hover:text-[#ff4858] transition-colors text-sm font-medium">Insights</Link>
                <Link to="/contact" className="block text-white/70 hover:text-primary transition-colors text-sm font-medium">Contact</Link>
              </div>
            </div>
            <div>
              <h4 className="text-sm uppercase tracking-wider text-white font-bold mb-6">Follow</h4>
              <div className="flex gap-4">
                <a href="https://www.linkedin.com/in/kara-prosser-26960977/" target="_blank" rel="noopener noreferrer" className="w-12 h-12 bg-white/10 hover:bg-primary rounded-full flex items-center justify-center transition-colors">
                  <i className="ri-linkedin-fill text-white text-xl"></i>
                </a>
              </div>
            </div>
          </div>
          <div className="border-t border-white/10 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-sm text-white/40 font-medium">© 2026 Design For Consulting</p>
            <div className="flex gap-6">
              <a href="#" className="text-white/40 hover:text-primary transition-colors text-sm font-medium">Privacy</a>
              <a href="#" className="text-white/40 hover:text-primary transition-colors text-sm font-medium">Terms</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
